// Assembly Weights Editor — edit each model's assembly TEMPLATE, which lives in the production sheets as
// the "<model>_SLOT" boat (e.g. 40_SLOT): rename/reweight tasks, add tasks and sections, remove them.
// Edits touch that _SLOT boat only, never a real boat's rows, so existing boats and Assembly Progress are
// untouched; boats made afterward (by cloning _SLOT) inherit the changes. New rows are inserted inside
// the _SLOT block, so the sheet order is never disturbed.

import { useMemo, useState, type DragEvent } from 'react';
import type { AssemblyTemplateSection } from '@technohull/shared';
import {
  useAddTemplateRow,
  useAssemblyTemplate,
  useDeleteTemplateRow,
  useMoveTemplateRow,
  useSetTemplateCell,
} from '../features/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { EmptyState, ErrorState, InlineError, LoadingState, Modal, PageHeader, SyncLabel } from '../components/ui.js';
import { IconBack, IconTrash } from '../components/icons.js';
import { useIsPhone } from '../lib/media.js';

const FAMILY_LABEL: Record<string, string> = {
  '40-50': '40 / 50',
  'GT7-GTX-38': 'GT7 / GTX / 38',
  '45-47-XPD': '45 / 47 / XPD',
};

type AddState =
  | { kind: 'task'; sheetId: string; parentRowId: number; name: string; weight: string }
  | { kind: 'section'; sheetId: string; parentRowId: number; name: string }
  | null;

export function AssemblyWeights() {
  const { can } = useAuth();
  const canWrite = can('production:write');
  const q = useAssemblyTemplate();
  const setCell = useSetTemplateCell();
  const addRow = useAddTemplateRow();
  const delRow = useDeleteTemplateRow();
  const move = useMoveTemplateRow();

  const families = q.data?.families ?? [];
  const [family, setFamily] = useState('');
  const active = useMemo(() => families.find((f) => f.family === family) ?? families[0], [families, family]);
  const [add, setAdd] = useState<AddState>(null);
  const [confirmDel, setConfirmDel] = useState<{ sheetId: string; rowId: number; label: string; isSection: boolean } | null>(null);
  const [drag, setDrag] = useState<{ rowId: number; sectionRowId: number } | null>(null);

  const saving = setCell.isPending || addRow.isPending || delRow.isPending || move.isPending;

  const submitAdd = () => {
    if (!add || !add.name.trim()) return;
    if (add.kind === 'task') {
      addRow.mutate({ sheetId: add.sheetId, parentRowId: add.parentRowId, kind: 'task', name: add.name.trim(), weight: Number(add.weight) || 0 });
    } else {
      addRow.mutate({ sheetId: add.sheetId, parentRowId: add.parentRowId, kind: 'section', name: add.name.trim() });
    }
    setAdd(null);
  };

  return (
    <>
      <PageHeader title="Assembly Weights Editor">
        {q.data && <span className="muted">Model templates · season {q.data.season}</span>}
        {saving && <span className="muted" style={{ fontSize: 11 }}>Saving…</span>}
        <button className="btn btn-sm" disabled={q.isFetching} onClick={() => void q.refetch()}><SyncLabel busy={q.isFetching} /></button>
      </PageHeader>
      {(setCell.error ?? addRow.error ?? delRow.error ?? move.error) && <InlineError error={setCell.error ?? addRow.error ?? delRow.error ?? move.error} onDismiss={() => { setCell.reset(); addRow.reset(); delRow.reset(); move.reset(); }} />}

      {q.isLoading && <LoadingState label="Loading model templates…" />}
      {q.error && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}

      {q.data && (
        <>
          <div className="wb-tabs" role="tablist" aria-label="Model family">
            {families.map((f) => (
              <button key={f.family} role="tab" aria-selected={active?.family === f.family} className={`wb-tab ${(active?.family === f.family) ? 'on' : ''}`}
                // phones scroll the tab strip sideways; keep the picked family's tab in view
                onClick={(e) => { setFamily(f.family); e.currentTarget.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' }); }}>
                {FAMILY_LABEL[f.family] ?? f.family}
              </button>
            ))}
          </div>

          {!active && <EmptyState title="No production sheets" />}
          {active && active.ok === false && (
            <EmptyState title="Couldn’t read this sheet" hint="Its Parent check / Primary Column / Weights columns weren’t found." />
          )}
          {active && active.ok !== false && active.models.length === 0 && (
            <EmptyState title="No models found" hint="This production sheet has no “Model” header rows - see the sheet structure below." />
          )}

          {active && active.diag && (
            <details className="awt-diag" open={active.models.length === 0}>
              <summary>Sheet structure - {active.diag.total} rows (diagnostic)</summary>
              <div className="awt-diag-body">
                <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                  Rows grouped by their <b>Parent check</b> value. Each model’s template is its{' '}
                  <code>&lt;model&gt;_SLOT</code> boat (e.g. <code>40_SLOT</code>) and its section / task rows.
                </p>
                <div className="awt-diag-counts">
                  {Object.entries(active.diag.pcCounts).map(([k, n]) => (
                    <span key={k} className="awt-chip"><b>{k}</b> · {n}</span>
                  ))}
                </div>

                {active.diag.slotHits && active.diag.slotHits.length > 0 && (
                  <>
                    <p className="muted" style={{ fontSize: 12, margin: '10px 0 4px' }}>Rows mentioning “SLOT”:</p>
                    <div className="awt-diag-counts">
                      {active.diag.slotHits.map((h) => (
                        <span key={h.row} className="awt-chip">#{h.row} · {h.pc || 'leaf'} · <b>{h.name}</b></span>
                      ))}
                    </div>
                  </>
                )}

                {active.diag.boats && active.diag.boats.length > 0 && (
                  <>
                    <p className="muted" style={{ fontSize: 12, margin: '10px 0 4px' }}>
                      All {active.diag.boats.length} boat titles (name · SAP) - the template boat is one of these:
                    </p>
                    <div className="awt-diag-counts">
                      {active.diag.boats.map((b, i) => (
                        <span key={i} className="awt-chip">{b.name || '(blank)'}{b.sap ? '' : ' · no SAP'}</span>
                      ))}
                    </div>
                  </>
                )}

                <p className="muted" style={{ fontSize: 12, margin: '10px 0 4px' }}>First rows:</p>
                <table className="awt-diag-tbl">
                  <thead><tr><th>#</th><th>Parent check</th><th>Primary Column</th><th>Weights</th></tr></thead>
                  <tbody>
                    {active.diag.head.map((r, i) => (
                      <tr key={i} className={r.pc === 'Model' ? 'is-model' : r.pc === 'Boat_Title' ? 'is-boat' : ''}>
                        <td className="muted">{i + 1}</td>
                        <td>{r.pc || <span className="muted">-</span>}</td>
                        <td>{r.name || <span className="muted">-</span>}</td>
                        <td>{r.w || <span className="muted">-</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {active && active.models.map((m) => (
            <div key={m.modelRowId} className="awt-model">
              <div className="awt-model-hd">
                <span>{m.model || '(unnamed model)'}</span>
                {m.slotTitle && <span className="awt-slot-tag">template · {m.slotTitle}</span>}
                <span className="c">{m.sections.reduce((n, s) => n + s.tasks.length, 0)} task(s) · {Math.round(m.sections.reduce((n, s) => n + s.tasks.reduce((a, t) => a + t.weight, 0), 0) * 10) / 10} h</span>
              </div>

              {m.slotRowId === 0 && (
                <div className="awt-seed">
                  <div>
                    <b>No <code>_SLOT</code> template boat for this model.</b>
                    <p className="muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
                      This model needs a reserved slot boat (e.g. <code>{m.model?.split(' ').pop()}_SLOT 1</code>)
                      under it in the production sheet. The first slot becomes this model’s editable template.
                    </p>
                  </div>
                </div>
              )}
              {m.slotRowId !== 0 && m.sections.length === 0 && (
                <div className="awt-seed">
                  <b>Empty template.</b>
                  <span className="muted" style={{ fontSize: 12 }}>Add a section to start building it.</span>
                </div>
              )}

              {m.sections.map((s) => (
                <SectionBlock
                  key={`${s.rowId}:${s.synthetic ? 'syn' : 'sec'}`}
                  section={s}
                  canWrite={canWrite}
                  onCell={(rowId, patch) => setCell.mutate({ sheetId: active.sheetId, rowId, ...patch })}
                  onAddTask={() => setAdd({ kind: 'task', sheetId: active.sheetId, parentRowId: s.rowId, name: '', weight: '' })}
                  onDelTask={(rowId, label) => setConfirmDel({ sheetId: active.sheetId, rowId, label, isSection: false })}
                  onDelSection={() => setConfirmDel({ sheetId: active.sheetId, rowId: s.rowId, label: s.name, isSection: true })}
                  addingHere={add?.kind === 'task' && add.parentRowId === s.rowId}
                  add={add}
                  setAdd={setAdd}
                  submitAdd={submitAdd}
                  drag={drag}
                  setDrag={setDrag}
                  onMoveTask={(rowId, siblingId, above) => move.mutate({ sheetId: active.sheetId, rowId, siblingId, above })}
                />
              ))}

              {canWrite && m.slotRowId !== 0 && (
                <div className="awt-add">
                  {add?.kind === 'section' && add.parentRowId === m.modelRowId ? (
                    <>
                      <input className="field awt-add-nm" autoFocus placeholder="New section name" enterKeyHint="done"
                        value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); else if (e.key === 'Escape') setAdd(null); }} />
                      <button className="btn btn-sm btn-primary" disabled={!add.name.trim()} onClick={submitAdd}>Add section</button>
                      <button className="btn btn-sm btn-ghost" onClick={() => setAdd(null)}>Cancel</button>
                    </>
                  ) : (
                    <button className="btn btn-sm btn-ghost" onClick={() => setAdd({ kind: 'section', sheetId: active.sheetId, parentRowId: m.modelRowId, name: '' })}>+ Section</button>
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {confirmDel && (
        <Modal title="Remove from template?" onClose={() => setConfirmDel(null)}
          footer={<>
            <button className="btn btn-ghost" onClick={() => setConfirmDel(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => { delRow.mutate({ sheetId: confirmDel.sheetId, rowId: confirmDel.rowId }); setConfirmDel(null); }}>Remove</button>
          </>}>
          <p style={{ marginTop: 0 }}>Remove <b>{confirmDel.label || 'this row'}</b> from the model template?</p>
          <p className="muted" style={{ fontSize: 12 }}>
            {confirmDel.isSection ? 'The section and all its tasks are removed from the template. ' : ''}
            Boats already in production are not affected - only the template changes.
          </p>
        </Modal>
      )}

    </>
  );
}

/** phones: a section or task name that wraps onto as many lines as its text needs, instead of a one-line
 *  input that clips long names. The textarea is sized by a hidden copy of its text (the wrapper's ::after,
 *  fed by data-value), so it re-flows on rotation with no measuring. Same save path as the desktop input:
 *  commit on blur when the text changed and isn't blank; Enter commits instead of adding a line. */
function GrowName({ value, label, disabled, onCommit }: {
  value: string;
  label: string;
  disabled: boolean;
  onCommit: (name: string) => void;
}) {
  return (
    <span className="awt-grow" data-value={value}>
      <textarea className="nm" rows={1} aria-label={label} disabled={disabled} defaultValue={value} enterKeyHint="done"
        onInput={(e) => { const p = e.currentTarget.parentElement; if (p) p.dataset.value = e.currentTarget.value; }}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); e.currentTarget.blur(); } }}
        onBlur={(e) => {
          // a name is one line: a pasted line break becomes a space, as a text input would drop it
          const v = e.target.value.replace(/\s*[\r\n]+\s*/g, ' ');
          if (v.trim() && v !== value) onCommit(v.trim());
        }} />
    </span>
  );
}

function SectionBlock({
  section, canWrite, onCell, onAddTask, onDelTask, onDelSection, addingHere, add, setAdd, submitAdd, drag, setDrag, onMoveTask,
}: {
  section: AssemblyTemplateSection;
  canWrite: boolean;
  onCell: (rowId: number, patch: { name?: string; weight?: number }) => void;
  onAddTask: () => void;
  onDelTask: (rowId: number, label: string) => void;
  onDelSection: () => void;
  addingHere: boolean;
  add: AddState;
  setAdd: (a: AddState) => void;
  submitAdd: () => void;
  drag: { rowId: number; sectionRowId: number } | null;
  setDrag: (d: { rowId: number; sectionRowId: number } | null) => void;
  onMoveTask: (rowId: number, siblingId: number, above: boolean) => void;
}) {
  const isPhone = useIsPhone();
  // only allow a drop from a drag that started in THIS section (reorder within a section)
  const canDrop = (t: { rowId: number }) => !!drag && drag.sectionRowId === section.rowId && drag.rowId !== t.rowId;
  const handleDrop = (e: DragEvent, targetRowId: number) => {
    if (!drag || drag.sectionRowId !== section.rowId || drag.rowId === targetRowId) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const above = e.clientY < r.top + r.height / 2;
    onMoveTask(drag.rowId, targetRowId, above);
    setDrag(null);
  };
  return (
    <>
      <div className="awt-sec-hd">
        {section.synthetic ? (
          <span className="nm" style={{ opacity: 0.7 }}>{section.name}</span>
        ) : isPhone ? (
          <GrowName key={`s:${section.rowId}:${section.name}`} value={section.name} label="Section name" disabled={!canWrite}
            onCommit={(name) => onCell(section.rowId, { name })} />
        ) : (
          <input className="nm" aria-label="Section name" disabled={!canWrite} defaultValue={section.name} key={`s:${section.rowId}:${section.name}`}
            onBlur={(e) => { if (e.target.value.trim() && e.target.value !== section.name) onCell(section.rowId, { name: e.target.value.trim() }); }} />
        )}
        {canWrite && !section.synthetic && (
          <button className="awt-del" title="Remove section" aria-label={`Remove section ${section.name}`} onClick={onDelSection}><IconTrash size={13} /></button>
        )}
      </div>

      {section.tasks.map((t, i) => {
        const prev = section.tasks[i - 1];
        const next = section.tasks[i + 1];
        return (
          <div key={t.rowId} className={`awt-task ${drag?.rowId === t.rowId ? 'dragging' : ''}`}
            onDragOver={(e) => { if (canDrop(t)) e.preventDefault(); }}
            onDrop={(e) => handleDrop(e, t.rowId)}>
            {canWrite
              ? <span className="awt-grip" title="Drag to reorder" draggable
                  onDragStart={() => setDrag({ rowId: t.rowId, sectionRowId: section.rowId })}
                  onDragEnd={() => setDrag(null)}>⠿</span>
              : <span />}
            {/* touch can't start an HTML5 drag, so coarse pointers reorder with these (hidden on a mouse) */}
            {canWrite && (
              <span className="awt-moves">
                <button type="button" className="awt-move awt-move--up" aria-label={`Move ${t.name} up`} title="Move up"
                  disabled={!prev} onClick={() => prev && onMoveTask(t.rowId, prev.rowId, true)}><IconBack size={18} /></button>
                <button type="button" className="awt-move awt-move--down" aria-label={`Move ${t.name} down`} title="Move down"
                  disabled={!next} onClick={() => next && onMoveTask(t.rowId, next.rowId, false)}><IconBack size={18} /></button>
              </span>
            )}
            {isPhone ? (
              <GrowName key={`n:${t.rowId}:${t.name}`} value={t.name} label="Task name" disabled={!canWrite}
                onCommit={(name) => onCell(t.rowId, { name })} />
            ) : (
              <input className="nm" aria-label="Task name" disabled={!canWrite} defaultValue={t.name} key={`n:${t.rowId}:${t.name}`}
                onBlur={(e) => { if (e.target.value.trim() && e.target.value !== t.name) onCell(t.rowId, { name: e.target.value.trim() }); }} />
            )}
            <input className="field wt" type="number" inputMode="decimal" min={0} step="0.5" aria-label={`Hours for ${t.name}`} disabled={!canWrite} defaultValue={t.weight} key={`w:${t.rowId}:${t.weight}`}
              onBlur={(e) => { const n = Number(e.target.value); if (!Number.isNaN(n) && n !== t.weight) onCell(t.rowId, { weight: n }); }} />
            {canWrite
              ? <button className="awt-del" title="Remove task" aria-label={`Remove ${t.name}`} onClick={() => onDelTask(t.rowId, t.name)}><IconTrash size={13} /></button>
              : <span />}
          </div>
        );
      })}

      {canWrite && (
        <div className="awt-add">
          {addingHere && add?.kind === 'task' ? (
            <>
              <input className="field awt-add-nm" autoFocus placeholder="New task name"
                value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); else if (e.key === 'Escape') setAdd(null); }} />
              <input className="field awt-add-wt" type="number" inputMode="decimal" min={0} step="0.5" placeholder="hrs" enterKeyHint="done"
                value={add.weight} onChange={(e) => setAdd({ ...add, weight: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); else if (e.key === 'Escape') setAdd(null); }} />
              <button className="btn btn-sm btn-primary" disabled={!add.name.trim()} onClick={submitAdd}>Add task</button>
              <button className="btn btn-sm btn-ghost" onClick={() => setAdd(null)}>Cancel</button>
            </>
          ) : (
            <button className="btn btn-sm btn-ghost" onClick={onAddTask}>+ Task</button>
          )}
        </div>
      )}
    </>
  );
}
